import { memo, type ComponentPropsWithoutRef } from "react";
import { defineMessages, useIntl } from "react-intl";

import { useForm } from "common/core/form";
import { TextInput, EmailTextInput } from "common/core/form/text";
import { CheckboxLabel, Checkbox } from "common/core/form/option";
import { isAriaInvalid, FieldErrorMessage, emailPatternValidation } from "common/core/form/error";
import { UserTermsOfService, MasterPrivacyPolicyUrl } from "common/tos";
import { useCompanyName } from "util/feature_detection";

import {
  Form,
  Subheader,
  Fields,
  CheckboxContainer,
  usePromiseSubmitLock,
  Actions,
  SpacedMultipartFormRow,
} from "./form";

export type FormValues = {
  firstName: string;
  lastName: string;
  email: string;
  personallyKnown: "yes";
  inUS: "yes";
};
type Props = {
  onSubmit: (formValues: FormValues) => Promise<unknown>;
  onBack: ComponentPropsWithoutRef<typeof Actions>["onBack"];
};

const MESSAGES = defineMessages({
  formHeader: {
    id: "0bc4c339-93b4-41c1-8ae2-074b36b7f476",
    defaultMessage: "The witness is fully remote (neither with me, nor the signer).",
  },
  formInstructions: {
    id: "d70c93d8-a9be-47c0-ab59-351b380cb278",
    defaultMessage: "Please enter their name and email to send them a link to join the meeting.",
  },
  firstName: {
    id: "0b487ade-bd05-4080-b837-32c3e7f6cc5f",
    defaultMessage: "First Name",
  },
  lastName: {
    id: "0d596358-651d-4cb4-a323-c67bded9f3be",
    defaultMessage: "Last Name",
  },
  email: {
    id: "bb72ecb5-7366-4dbb-8592-7016e69c40f8",
    defaultMessage: "Email Address",
  },
  saveButton: {
    id: "98b4d160-8b94-4f65-9b95-20ab14dd8c63",
    defaultMessage: "Send Meeting Link",
  },
  personallyKnown: {
    id: "b7178475-705c-4a4d-b6cd-e3751edd99b6",
    defaultMessage:
      "I personally know the witness named above and by checking this box I agree to the {companyName} {terms} and {privacy}.",
  },
  inUS: {
    id: "aac88733-57e1-440d-9ab2-247d73efdfc6",
    defaultMessage:
      "I confirm that the witness is physically located in the US and is a US resident.",
  },
  requiredAgreement: {
    id: "4c2bb3c1-c30e-4729-a2ab-f422a00bb277",
    defaultMessage: "You must agree to these before proceeding.",
  },
});

function CollectFullyRemoteWitnessInformation({ onSubmit, onBack }: Props) {
  const intl = useIntl();
  const form = useForm<FormValues>();
  const [loading, handleSubmit] = usePromiseSubmitLock(onSubmit);
  const { errors } = form.formState;
  const companyName = useCompanyName();
  return (
    <Form onSubmit={form.handleSubmit(handleSubmit)}>
      <Fields>
        <Subheader>{intl.formatMessage(MESSAGES.formHeader)}</Subheader>

        <p>{intl.formatMessage(MESSAGES.formInstructions)}</p>

        <SpacedMultipartFormRow>
          <TextInput
            placeholder={intl.formatMessage(MESSAGES.firstName)}
            aria-invalid={isAriaInvalid(errors.firstName)}
            {...form.register("firstName", { required: true })}
          />

          <TextInput
            placeholder={intl.formatMessage(MESSAGES.lastName)}
            aria-invalid={isAriaInvalid(errors.lastName)}
            {...form.register("lastName", { required: true })}
          />
        </SpacedMultipartFormRow>

        <EmailTextInput
          placeholder={intl.formatMessage(MESSAGES.email)}
          aria-invalid={isAriaInvalid(errors.email)}
          {...form.register("email", {
            required: true,
            pattern: emailPatternValidation(intl),
          })}
        />

        <CheckboxContainer>
          <div>
            <CheckboxLabel
              label={
                intl.formatMessage(MESSAGES.personallyKnown, {
                  terms: <UserTermsOfService />,
                  privacy: <MasterPrivacyPolicyUrl />,
                  companyName,
                }) as string
              }
              checkbox={
                <Checkbox
                  value="yes"
                  aria-invalid={isAriaInvalid(errors.personallyKnown)}
                  {...form.register("personallyKnown", { required: true })}
                />
              }
            />
          </div>

          <div>
            <CheckboxLabel
              label={intl.formatMessage(MESSAGES.inUS)}
              checkbox={
                <Checkbox
                  value="yes"
                  aria-invalid={isAriaInvalid(errors.inUS)}
                  {...form.register("inUS", { required: true })}
                />
              }
            />
          </div>

          <div>
            <FieldErrorMessage
              inputName="agreements"
              message={
                errors.personallyKnown || errors.inUS
                  ? intl.formatMessage(MESSAGES.requiredAgreement)
                  : undefined
              }
            />
          </div>
        </CheckboxContainer>
      </Fields>

      <Actions onBack={onBack} isLoading={loading}>
        {intl.formatMessage(MESSAGES.saveButton)}
      </Actions>
    </Form>
  );
}

export default memo(CollectFullyRemoteWitnessInformation);
